import orderBy from 'lodash/orderBy';
import { RawSQL, sqlF, sqlIdentifier, sqlJoinF } from '../sqlTemplate';
import {
  ReferenceDataSchemaDefinition,
  UniqueKeyTypeList,
  uniqueKeyTypes,
} from './ReferenceDataSchemaInterface';
import { Immutable } from 'immer';

export const STABLE_ROW_ID_PREFIX = 'srid_1';

/**
 * Column name for stable row IDs in reference data.
 */
export const STABLE_ROW_ID_COL = '__stable_row_id';
export const STABLE_ROW_ID_COL_I = sqlIdentifier(STABLE_ROW_ID_COL);
// search an error msg for this string
export const STABLE_ROW_ID_COL_MISSING = `does not have a column named "${STABLE_ROW_ID_COL}"`;

/**
 * Returns a SQL expression for stable row IDs based on a set of unique key
 * columns. The flagship example is stable row IDs for reference data. It's also
 * used to generate group IDs for value mappings.
 *
 * A stable row ID is a string that uniquely identifies a row, and is stable
 * across multiple revisions -- as long as the unique key values are unchanged.
 *
 * 1) First, we define a canonical ordering for the unique key columns by
 *    sorting them alphabetically.
 *
 * 2) Then, we place tuples containing non-null key-value pairs into a JSON
 *    array and take the MD5 hash of that array. Using only non-null values lets
 *    us keep IDs stable when new key columns are added. For example, the
 *    following two sets of keys will have the same ID:
 *
 *    - { "country": "US", "state": "CA" }
 *    - { "country": "US", "state": "CA", "city": null }
 *
 * 3) Finally, we concatenate a prefix with the MD5 hash, so that the generated
 *    ID has a human-readable prefix. This also gives us an escape hatch for
 *    future updates to the stable row ID spec, in which case we'd update the
 *    prefix to, for example, `srid_2`.
 *
 * Examples: srid_15a3f6f3c98295d60ea83de8e28b8b56f
 *   srid_12b35615ff1cc26d1a4e5f05a4f6fa035
 */
export function stableRowIdSql(
  table: string,
  uniqueKeys: Immutable<UniqueKeyTypeList> = [],
  prefix = STABLE_ROW_ID_PREFIX
): RawSQL {
  const orderedKeys = orderBy(uniqueKeys, (k) => k.name);

  // Instead of constructing an array of key-value pairs and then serializing
  // it to JSON, we serialize individual values and then construct a JSON
  // string manually -- this gives us greater control over the way each value
  // gets serialized.
  return sqlF`CONCAT('${prefix}', MD5(CONCAT('[', LIST_AGGR(LIST_FILTER([${sqlJoinF(
    orderedKeys.map(({ name, ty }) => {
      const column = sqlF`${sqlIdentifier(table)}.${sqlIdentifier(name)}`;
      let valueExpr = sqlF`TO_JSON(${column})`;

      // As of v0.8.1, DuckDB includes a single trailing zero when serializing
      // decimals (e.g. 1.0 -> "1.0", but 1.2300 -> "1.23") so we need to trim
      // those (otherwise 1 != 1.0).
      if (ty === 'number' || ty === 'integer') {
        valueExpr = trimTrailingZeros(valueExpr);
      }

      return sqlF`CASE WHEN ${column} IS NOT NULL THEN ${serializeKeyValuePair(
        name,
        valueExpr
      )} ELSE NULL END`;
    }),
    ', '
  )}], x -> x IS NOT NULL), 'STRING_AGG', ','), ']')))`;
}

/**
 * The same as `stableRowIdSql` but accepts a `ReferenceDataSchemaDefinition`,
 * and determines the unique key columns from that.
 */
export function stableRowIdSqlForSchema(
  table: string,
  schema: Immutable<ReferenceDataSchemaDefinition>
): RawSQL {
  return stableRowIdSql(table, uniqueKeyTypes(schema));
}

function serializeKeyValuePair(key: string, valueExpr: RawSQL): RawSQL {
  return sqlF`'[' || TO_JSON('${key}') || ',' || ${valueExpr} || ']'`;
}

function trimTrailingZeros(expr: RawSQL): RawSQL {
  return sqlF`REGEXP_REPLACE(${expr}, '\\.0+$', '')`;
}
